import _extends from "@babel/runtime/helpers/esm/extends";
import { processStream } from "../../stream/index.js";
import { createLocalId } from "../createLocalId.js";
import { getMessages, createRuntimeError, getErrorMessage, findAssistantMessageIdsForRetry, removeAssistantMessageIds } from "./useChatControllerHelpers.js";
import { getFinishMessage } from "../../stream/streamHelpers.js";
export function createSendMessageActions(params) {
  const {
    store,
    storeUnknown,
    runtimeRef,
    setRuntimeError,
    assistantMessageIdByUserMessageIdRef
  } = params;

  // Synchronous flag to guard against double-invocation in React 18 Strict Mode.
  // `store.state.isStreaming` is updated synchronously, but React 18 may re-trigger
  // effects before the store subscription causes a re-render that makes the guard
  // effective. This flag is set immediately before any async work.
  let isSending = false;
  const attachmentsByUserMessageId = new Map();
  function pruneAttachmentsByMessageIds(messageIds) {
    if (attachmentsByUserMessageId.size === 0) {
      return;
    }
    const activeMessageIds = new Set(messageIds);
    for (const messageId of attachmentsByUserMessageId.keys()) {
      if (!activeMessageIds.has(messageId)) {
        attachmentsByUserMessageId.delete(messageId);
      }
    }
  }
  async function processStreamWithReconnect(stream, conversationId, abortController) {
    const adapter = runtimeRef.current.adapter;
    const shouldDeferFinish = Boolean(adapter.reconnectToStream);
    const processOptions = {
      conversationId,
      signal: abortController.signal,
      flushInterval: runtimeRef.current.streamFlushInterval,
      onToolCall: runtimeRef.current.onToolCall,
      onFinish: shouldDeferFinish ? undefined : runtimeRef.current.onFinish,
      onData: runtimeRef.current.onData
    };
    const emitDeferredFinish = async result => {
      if (!shouldDeferFinish || !runtimeRef.current.onFinish) {
        return;
      }
      await runtimeRef.current.onFinish({
        message: getFinishMessage(storeUnknown, result.messageId, conversationId, result.status),
        messages: getMessages(storeUnknown),
        isAbort: result.isAbort,
        isDisconnect: result.isDisconnect,
        isError: result.isError,
        finishReason: result.finishReason
      });
    };
    const result = await processStream(store, stream, processOptions);
    if (!result.isDisconnect || !result.messageId || !adapter.reconnectToStream || abortController.signal.aborted) {
      await emitDeferredFinish(result);
      return result;
    }
    try {
      store.setStreaming(true);
      const reconnectStream = await adapter.reconnectToStream({
        conversationId,
        messageId: result.messageId,
        signal: abortController.signal
      });
      if (!reconnectStream || abortController.signal.aborted) {
        store.setStreaming(false);
        await emitDeferredFinish(result);
        return result;
      }
      return await processStream(store, reconnectStream, _extends({}, processOptions, {
        onFinish: runtimeRef.current.onFinish,
        messageId: result.messageId,
        reconnectFromSequence: result.nextSequence,
        seenEventIds: result.seenEventIds
      }));
    } catch (error) {
      store.setStreaming(false);
      if (abortController.signal.aborted) {
        const cancelledResult = {
          messageId: result.messageId,
          status: 'cancelled',
          isAbort: true,
          isDisconnect: false,
          isError: false
        };
        await emitDeferredFinish(cancelledResult);
        return cancelledResult;
      }
      store.setError(createRuntimeError('STREAM_ERROR', getErrorMessage('Unable to reconnect to the message stream.', error), 'stream', true, true, {
        messageId: result.messageId,
        conversationId
      }));
      const errorResult = {
        messageId: result.messageId,
        status: 'error',
        isAbort: false,
        isDisconnect: true,
        isError: true
      };
      await emitDeferredFinish(errorResult);
      return errorResult;
    }
  }
  async function sendExistingMessage(message, attachments) {
    if (isSending || store.state.isStreaming) {
      return;
    }
    const conversationId = message.conversationId ?? store.state.activeConversationId;
    const nextMessage = _extends({}, message, {
      conversationId,
      role: 'user',
      status: 'sending'
    });
    isSending = true;
    store.addMessage(nextMessage);
    store.setStreaming(true);
    store.setError(null);
    store.clearMessageError(nextMessage.id);
    if (attachments && attachments.length > 0) {
      attachmentsByUserMessageId.set(nextMessage.id, attachments);
    } else {
      attachmentsByUserMessageId.delete(nextMessage.id);
    }
    const abortController = new AbortController();
    store.setActiveStreamAbortController(abortController);
    try {
      const stream = await runtimeRef.current.adapter.sendMessage({
        conversationId,
        message: nextMessage,
        messages: getMessages(storeUnknown),
        attachments,
        signal: abortController.signal
      });
      const result = await processStreamWithReconnect(stream, conversationId, abortController);
      let status;
      if (result.status === 'cancelled') {
        status = 'cancelled';
      } else if (result.status === 'error') {
        status = 'error';
      } else {
        status = 'sent';
      }
      store.updateMessage(nextMessage.id, {
        status
      });
      if (status === 'sent') {
        attachmentsByUserMessageId.delete(nextMessage.id);
        store.clearMessageError(nextMessage.id);
      }
      if (result.messageId) {
        assistantMessageIdByUserMessageIdRef.current.set(nextMessage.id, result.messageId);
      }
      if (result.isError && store.state.error) {
        runtimeRef.current.onError?.(store.state.error);
      }
    } catch (error) {
      store.updateMessage(nextMessage.id, {
        status: abortController.signal.aborted ? 'cancelled' : 'error'
      });
      if (abortController.signal.aborted) {
        store.setStreaming(false);
        return;
      }
      if (store.state.error?.source === 'stream') {
        runtimeRef.current.onError?.(store.state.error);
        return;
      }
      setRuntimeError((() => {
        const runtimeError = createRuntimeError('SEND_ERROR', getErrorMessage('Unable to send the message.', error), 'send', true, true, {
          messageId: nextMessage.id,
          conversationId
        });
        store.setMessageError(nextMessage.id, runtimeError);
        return runtimeError;
      })());
      store.setStreaming(false);
    } finally {
      isSending = false;
      if (store.state.activeStreamAbortController === abortController) {
        store.setActiveStreamAbortController(null);
      }
    }
  }
  async function sendMessage(input) {
    const message = {
      id: input.id ?? createLocalId(),
      conversationId: input.conversationId ?? store.state.activeConversationId,
      role: 'user',
      parts: input.parts,
      metadata: input.metadata,
      author: input.author,
      createdAt: input.createdAt ?? new Date().toISOString()
    };
    await sendExistingMessage(message, input.attachments);
  }
  async function retry(messageId) {
    // Hoist the streaming guard above any destructive work so retrying while a
    // stream is in flight is a clean no-op rather than wiping prior assistant
    // messages and then silently bailing inside `sendExistingMessage` (#2).
    if (isSending || store.state.isStreaming) {
      return;
    }
    const message = store.state.messagesById[messageId];
    if (!message || message.role !== 'user') {
      return;
    }
    const assistantMessageIds = findAssistantMessageIdsForRetry(storeUnknown, messageId, assistantMessageIdByUserMessageIdRef.current);
    removeAssistantMessageIds(storeUnknown, assistantMessageIds, assistantMessageIdByUserMessageIdRef.current);
    pruneAttachmentsByMessageIds(store.state.messageIds);
    await sendExistingMessage(message, attachmentsByUserMessageId.get(messageId));
  }
  return {
    sendMessage,
    retry,
    pruneAttachmentsByMessageIds
  };
}